import { useState, useEffect, useMemo } from 'react';
import { Modal, Drawer, Select, Space, Typography, Input, message, Alert, Button, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import api from '../../api/client';
import { markingApi, ContainerSizePreset, CatalogSize, GtinBindBody, GtinSuggestion } from '../../api/marking';
import useIsMobile from '../../hooks/useIsMobile';

const { Text } = Typography;

interface Props {
  open: boolean;
  gtin: string;
  onBound: (catalogId: number) => void;
  onCancel: () => void;
}

interface CatalogItem {
  id: number;
  brand: string | null;
  model_name: string | null;
}

// Choice: existing = готовый OilProductSize, preset = фасовка из общего списка, custom = ручной ввод
type Choice =
  | { kind: 'existing'; size_id: number; label: string }
  | { kind: 'preset'; size: string }
  | { kind: 'custom' }
  | null;

const encodeChoice = (c: Choice): string | undefined => {
  if (!c) return undefined;
  if (c.kind === 'existing') return `id:${c.size_id}`;
  if (c.kind === 'preset') return `preset:${c.size}`;
  return 'custom';
};

const GtinBindModal: React.FC<Props> = ({ open, gtin, onBound, onCancel }) => {
  const isMobile = useIsMobile();
  const [catalogItems, setCatalogItems] = useState<CatalogItem[]>([]);
  const [existingSizes, setExistingSizes] = useState<CatalogSize[]>([]);
  const [presets, setPresets] = useState<ContainerSizePreset[]>([]);
  const [sizesLoading, setSizesLoading] = useState(false);

  const [catalogId, setCatalogId] = useState<number | null>(null);
  const [choice, setChoice] = useState<Choice>(null);
  const [customInput, setCustomInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState<GtinSuggestion | null>(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);

  // При открытии — сбросить и подгрузить справочники
  useEffect(() => {
    if (!open) return;
    api.get('/product-catalog').then(r => setCatalogItems(r.data));
    markingApi.getContainerSizePresets()
      .then(r => setPresets(r.data))
      .catch(() => setPresets([]));
    setCatalogId(null);
    setChoice(null);
    setCustomInput('');
    setExistingSizes([]);
    setSuggestion(null);
    if (gtin) {
      setSuggestionLoading(true);
      markingApi.suggestGtin(gtin)
        .then(r => setSuggestion(r.data))
        .catch(() => setSuggestion(null))
        .finally(() => setSuggestionLoading(false));
    }
  }, [open, gtin]);

  // При выборе товара — подгрузить его существующие размеры
  useEffect(() => {
    if (!catalogId) {
      setExistingSizes([]);
      setChoice(null);
      return;
    }
    setSizesLoading(true);
    setChoice(null);
    markingApi.getCatalogSizes(catalogId)
      .then(r => setExistingSizes(r.data))
      .catch(() => setExistingSizes([]))
      .finally(() => setSizesLoading(false));
  }, [catalogId]);

  // Опции селекта тары с группировкой
  const taraOptions = useMemo(() => {
    const usedSizes = new Set(existingSizes.map(s => s.container_size));
    const groups: any[] = [];

    if (existingSizes.length > 0) {
      groups.push({
        label: 'Уже привязанные',
        options: existingSizes.map(s => ({
          value: `id:${s.id}`,
          label: s.container_size,
        })),
      });
    }

    const presetOptions = presets
      .filter(p => !usedSizes.has(p.size))
      .map(p => ({ value: `preset:${p.size}`, label: p.size }));

    if (presetOptions.length > 0) {
      groups.push({
        label: 'Стандартные фасовки',
        options: presetOptions,
      });
    }

    groups.push({
      label: ' ',
      options: [{ value: 'custom', label: 'Другое…' }],
    });

    return groups;
  }, [existingSizes, presets]);

  const handleChoiceChange = (value: string) => {
    if (value === 'custom') {
      setChoice({ kind: 'custom' });
    } else if (value.startsWith('id:')) {
      const id = Number(value.slice(3));
      const size = existingSizes.find(s => s.id === id);
      setChoice({ kind: 'existing', size_id: id, label: size?.container_size || '' });
    } else if (value.startsWith('preset:')) {
      setChoice({ kind: 'preset', size: value.slice(7) });
    }
  };

  // Данные из ЧЗ полны — можно создать товар в 1 клик, если оператор не выбрал другой вариант
  const hasCompleteSuggestion = !!(
    suggestion
    && suggestion.status === 'needs_review'
    && suggestion.suggested_brand
    && suggestion.suggested_model_name
    && suggestion.suggested_container_size
    && !suggestion.suggested_catalog_id
  );

  const manualMode = catalogId !== null;

  const canSubmit = manualMode
    ? !!(catalogId && (
        (choice?.kind === 'existing') ||
        (choice?.kind === 'preset') ||
        (choice?.kind === 'custom' && customInput.trim().length > 0)
      ))
    : hasCompleteSuggestion;

  const handleCreateFromSuggestion = async () => {
    if (!suggestion?.suggested_brand || !suggestion?.suggested_model_name) return;
    setLoading(true);
    try {
      // Создаём товар через существующий catalog endpoint
      const { data: created } = await api.post('/product-catalog', {
        brand: suggestion.suggested_brand,
        model_name: suggestion.suggested_model_name,
        product_types: suggestion.suggested_product_type ? [suggestion.suggested_product_type] : ['прочее'],
      });
      // Привязываем с тарой (bindGtin сам добавит GTIN в product_catalog.gtin[])
      const body: GtinBindBody = suggestion.suggested_container_size
        ? { catalog_id: created.id, container_size: suggestion.suggested_container_size }
        : { catalog_id: created.id, container_size: '?' };
      await markingApi.bindGtin(gtin, body);
      message.success('Товар создан и GTIN привязан');
      onBound(created.id);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Ошибка создания товара');
    } finally {
      setLoading(false);
    }
  };

  const handleOk = async () => {
    // Manual: оператор сам выбрал товар в дропдауне → обычная привязка
    if (manualMode) {
      if (!catalogId || !choice) return;
      setLoading(true);
      try {
        let body: GtinBindBody;
        if (choice.kind === 'existing') {
          body = { catalog_id: catalogId, size_id: choice.size_id };
        } else if (choice.kind === 'preset') {
          body = { catalog_id: catalogId, container_size: choice.size };
        } else {
          body = { catalog_id: catalogId, container_size: customInput.trim() };
        }
        await markingApi.bindGtin(gtin, body);
        message.success('GTIN привязан к товару');
        onBound(catalogId);
      } catch (e: any) {
        message.error(e?.response?.data?.detail || 'Ошибка привязки');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Semi-auto: дропдауны не трогали, но ЧЗ нашёл товар целиком → создаём + привязываем
    if (hasCompleteSuggestion) {
      await handleCreateFromSuggestion();
    }
  };

  const okText = !manualMode && hasCompleteSuggestion ? 'Подтвердить' : 'Привязать';
  const body = (
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Text>GTIN: <Text strong>{gtin}</Text></Text>
        <Text type="secondary">
          GTIN однозначно идентифицирует товар + фасовку. Выберите товар и тару:
        </Text>

        {suggestionLoading && (
          <Alert
            type="info"
            showIcon
            icon={<Spin indicator={<LoadingOutlined style={{ fontSize: 18 }} spin />} />}
            message="Проверка товара в Честном Знаке…"
          />
        )}

        {suggestion && suggestion.status !== 'failed' && suggestion.suggested_brand && !hasCompleteSuggestion && (
          <Alert
            type={suggestion.status === 'auto_bound' ? 'success' : 'info'}
            showIcon
            message={
              <Space wrap>
                <Text>Честный Знак:</Text>
                <Text strong>
                  {suggestion.suggested_brand} {suggestion.suggested_model_name}
                  {suggestion.suggested_container_size && ` (${suggestion.suggested_container_size})`}
                </Text>
              </Space>
            }
            action={
              suggestion.suggested_catalog_id ? (
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    setCatalogId(suggestion.suggested_catalog_id!);
                    if (suggestion.suggested_size_id) {
                      const label = suggestion.suggested_container_size || '';
                      setChoice({ kind: 'existing', size_id: suggestion.suggested_size_id, label });
                    } else if (suggestion.suggested_container_size) {
                      setChoice({ kind: 'custom' });
                      setCustomInput(suggestion.suggested_container_size);
                    }
                  }}
                >
                  Принять
                </Button>
              ) : undefined
            }
          />
        )}

        {hasCompleteSuggestion && (
          <Alert
            type="success"
            showIcon
            message={
              <Space direction="vertical" size={2} style={{ width: '100%' }}>
                <Text>Честный Знак нашёл товар:</Text>
                <Text strong>
                  {suggestion!.suggested_brand} {suggestion!.suggested_model_name}
                  {suggestion!.suggested_container_size && ` (${suggestion!.suggested_container_size})`}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Проверьте, тот ли товар перед вами, и нажмите «Подтвердить». Или выберите другой товар вручную ниже.
                </Text>
              </Space>
            }
          />
        )}

        <Select
          showSearch
          placeholder="Выберите товар"
          style={{ width: '100%' }}
          value={catalogId}
          onChange={setCatalogId}
          filterOption={(input, option) =>
            (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
          }
          options={catalogItems.map(c => ({
            value: c.id,
            label: `${c.brand || ''} ${c.model_name || ''}`.trim(),
          }))}
        />

        <Select
          showSearch
          placeholder="Выберите тару"
          style={{ width: '100%' }}
          value={encodeChoice(choice)}
          onChange={handleChoiceChange}
          disabled={!catalogId || sizesLoading}
          loading={sizesLoading}
          options={taraOptions}
          filterOption={(input, option) =>
            (option?.label as string || '').toLowerCase().includes(input.toLowerCase())
          }
        />

        {choice?.kind === 'custom' && (
          <Input
            placeholder="Например: 4л, 208л, 5кг"
            value={customInput}
            onChange={e => setCustomInput(e.target.value)}
            autoFocus
          />
        )}
      </Space>
  );

  // На мобиле — полноэкранный Drawer снизу. Крупные кнопки в footer,
  // удобно тыкать большим пальцем. На десктопе — обычный Modal.
  if (isMobile) {
    return (
      <Drawer
        open={open}
        onClose={onCancel}
        title="Привязка GTIN к товару"
        placement="bottom"
        height="100%"
        styles={{ body: { padding: 16 } }}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>Отмена</Button>
            <Button
              type="primary"
              onClick={handleOk}
              disabled={!canSubmit}
              loading={loading}
              size="large"
            >
              {okText}
            </Button>
          </div>
        }
      >
        {body}
      </Drawer>
    );
  }

  return (
    <Modal
      open={open}
      title="Привязка GTIN к товару"
      onOk={handleOk}
      onCancel={onCancel}
      okText={okText}
      cancelText="Отмена"
      okButtonProps={{ disabled: !canSubmit, loading }}
    >
      {body}
    </Modal>
  );
};

export default GtinBindModal;
